import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { useParams } from "react-router-dom";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import CertificateRow from "./components/CertificateRow";
import {
  CertificateListItem,
  CertificatePickerProps,
  CertificateSelection,
} from "./types";

const PAGE_SIZE = 10;

const toSelection = (item: CertificateListItem): CertificateSelection => ({
  id: item.id,
  label: item.name,
});

const CertificatePickerCst: React.FC<CertificatePickerProps> = ({
  open,
  onClose,
  title,
  initialSelection,
  onConfirm,
}) => {
  const { t } = useTranslation("certificate-picker");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchSubmitted, setSearchSubmitted] = useState("");
  const [selected, setSelected] =
    useState<CertificateSelection[]>(initialSelection);

  const url = `${ServicesURL.getCertificates}${
    corporateId ? `?corporateId=${corporateId}` : ""
  }`;

  const { items, totalItems, totalPages, isLoading } =
    usePaginatedList<CertificateListItem>(url, {
      page,
      pageSize,
      search: searchSubmitted || undefined,
    });

  useEffect(() => {
    if (!open) return;
    setSelected(initialSelection);
    setPage(1);
    setSearchSubmitted("");
  }, [open, initialSelection]);

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => s.id)),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    const numericId = typeof id === "string" ? Number(id) : id;
    if (next) {
      const found = items.find((i) => i.id === numericId);
      if (found && !selected.some((s) => s.id === numericId)) {
        setSelected((prev) => [...prev, toSelection(found)]);
      }
    } else {
      setSelected((prev) => prev.filter((s) => s.id !== numericId));
    }
  };

  const handleSearch = (value: string) => {
    setSearchSubmitted(value);
    setPage(1);
  };

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  const canConfirm = selected.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "12px 24px 16px" }}>
        <SearchableMultiSelectListCst<CertificateListItem>
          selectionMode="multi"
          items={items}
          getId={(it) => it.id}
          renderRow={({ item }) => <CertificateRow item={item} />}
          selectedIds={selectedSet}
          onToggle={handleToggle}
          initialSearchValue={searchSubmitted}
          onSearchSubmit={handleSearch}
          searchPlaceholder={t("search-placeholder") || ""}
          searchButtonLabel={t("search-button") || ""}
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          isLoading={isLoading}
          selectedCountLabel={
            t("selected-count", { count: selected.length }) || ""
          }
          emptyLabel={t("empty") || ""}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        <ButtonPrimary size="md" onClick={handleConfirm} disabled={!canConfirm}>
          {t("confirm")}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};

export default CertificatePickerCst;
